import React from "react";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import { TrashIcon } from "../../../assets/images/TrashIcon";
import { CopyIcon } from "../../../assets/images/CopyIcon";
import { EyeIcon } from "../../../assets/images/EyeIcon";
import { Tick } from "../../../assets/images/Tick";
import { Cross } from "../../../assets/images/Cross";
import { COLORS } from "../../../assets/constants/styles";
import { useNavigate } from "react-router-dom";
import axios from "../../../api/axiosInstance";

export const ChatCard = ({
  chat,
  onRemove,
  onCopy,
  index,
  updateChatResult,
  isRunningTest,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const handleViewDetails = () => {
    if (chat.answerId) {
      navigate(`/logs/answers/${chat.answerId}`);
    }
  };

  const handleMarkStatus = async (status) => {
    if (!chat.answerId) return;

    try {
      const response = await axios.post(
        `/chat/answer/${chat.answerId}/status`,
        { status }
      );

      if (response.data) {
        updateChatResult(chat.id, { status });
      }
    } catch (error) {
      console.error("Error marking answer status:", error);
    }
  };

  const getErrorMessage = () => {
    if (!chat.error && !chat.errorKey) return null;

    if (chat.errorKey === "noQuestion") {
      return t("test.noQuestionError");
    } else if (chat.errorKey === "noRelevantWikisError") {
      return t("test.noRelevantWikisError");
    } else {
      return t("test.genericServerError");
    }
  };

  return (
    <CardContainer>
      <CardHeader>
        <ChatName>{t("test.testCardName", { number: index + 1 })}</ChatName>
        {!chat.answer && !chat.error && !isRunningTest && !chat.answerId && (
          <ButtonGroup>
            <ActionIconButton
              onClick={() => onCopy(chat)}
              aria-label={t("common.copy")}
            >
              <CopyIcon />
            </ActionIconButton>
            <ActionIconButton
              onClick={onRemove}
              aria-label={t("common.remove")}
            >
              <TrashIcon />
            </ActionIconButton>
          </ButtonGroup>
        )}
      </CardHeader>

      <MessagesContainer $hasResult={chat.answer || chat.error}>
        {chat.messages.map((message, idx) => (
          <MessageWrapper key={idx} $isClient={message.isClient}>
            <MessageBox $isClient={message.isClient}>
              <MessageAuthor>{message.sender}</MessageAuthor>
              <MessageText>{message.content}</MessageText>
            </MessageBox>
          </MessageWrapper>
        ))}
      </MessagesContainer>
      {(chat.answer || chat.error || chat.errorKey) && (
        <ResultContainer>
          {(chat.answer || chat.answer_text) && (
            <ResponseSection>
              <ResponseText>{chat.answer_text || chat.answer}</ResponseText>
            </ResponseSection>
          )}

          {(chat.error || chat.errorKey) && (
            <ErrorSection>
              <ErrorText>{getErrorMessage()}</ErrorText>
            </ErrorSection>
          )}
        </ResultContainer>
      )}
      {chat.answerId && (
        <ActionsBar>
          <ActionButton
            onClick={handleViewDetails}
            title={t("logs.viewDetails")}
            aria-label={t("logs.viewDetails")}
          >
            <EyeIcon />
          </ActionButton>

          <StatusButtons>
            <StatusButton
              onClick={() => handleMarkStatus("successful")}
              $active={chat.status === "successful"}
              $success
              title={t("test.markSuccessful")}
            >
              <Tick />
            </StatusButton>
            <StatusButton
              onClick={() => handleMarkStatus("unsuccessful")}
              $active={chat.status === "unsuccessful"}
              $error
              title={t("test.markUnsuccessful")}
            >
              <Cross />
            </StatusButton>
          </StatusButtons>
        </ActionsBar>
      )}

      {isRunningTest && (
        <LoadingOverlay>
          <LoaderWrapper>
            <Loader />
            <LoaderText>{t("test.runningTest")}</LoaderText>
          </LoaderWrapper>
        </LoadingOverlay>
      )}
    </CardContainer>
  );
};

const CardContainer = styled.div`
  background-color: ${COLORS.white};
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  overflow: hidden;
  position: relative;
  display: flex;
  flex-direction: column;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  height: 100%;
  min-height: 320px;
  max-height: 350px;
  border: 1px solid ${COLORS.lightViolet};

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 8px 24px rgba(74, 37, 213, 0.1);
  }
`;

const CardHeader = styled.div`
  padding: 16px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid ${COLORS.lightViolet};
  background-color: ${COLORS.lightViolet};
`;

const ChatName = styled.h3`
  font-size: 1rem;
  font-weight: 600;
  margin: 0;
  color: ${COLORS.violet};
  display: flex;
  align-items: center;
  cursor: pointer;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 8px;
`;

const ActionIconButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 6px;
  border-radius: 6px;
  color: ${COLORS.grey};
  transition: background-color 0.2s, color 0.2s;
  width: 3rem;
  height: 3rem;

  &:hover {
    background-color: rgba(74, 37, 213, 0.1);
    color: ${(props) =>
      props["aria-label"] === "Remove" ? COLORS.red : COLORS.violet};
  }
`;

const MessagesContainer = styled.div`
  padding: 16px;
  overflow-y: auto;
  flex-grow: 1;
  scrollbar-width: thin;
  scrollbar-color: ${COLORS.lightGrey} transparent;
  max-height: ${(props) => (props.$hasResult ? "120px" : "250px")};

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background-color: ${COLORS.lightGrey};
    border-radius: 10px;
  }
`;

const MessageWrapper = styled.div`
  display: flex;
  justify-content: ${(props) => (props.$isClient ? "flex-start" : "flex-end")};
  margin-bottom: 12px;
`;

const MessageBox = styled.div`
  background-color: ${(props) =>
    props.$isClient ? COLORS.lightestViolet : COLORS.violet};
  padding: 12px;
  border-radius: 12px;
  max-width: 85%;
  color: ${(props) => (props.$isClient ? COLORS.black : COLORS.white)};
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.05);
`;

const MessageAuthor = styled.div`
  font-size: 0.8rem;
  font-weight: 600;
  margin-bottom: 6px;
`;

const MessageText = styled.div`
  font-size: 0.875rem;
  font-weight: 400;
  line-height: 1.4;
  word-break: break-word;
`;

const LoadingOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(255, 255, 255, 0.85);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 5;
  backdrop-filter: blur(3px);
  border-radius: 12px;
`;

const LoaderWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
`;

const Loader = styled.div`
  border: 3px solid ${COLORS.lightestViolet};
  border-radius: 50%;
  border-top: 3px solid ${COLORS.violet};
  width: 36px;
  height: 36px;
  animation: spin 1s linear infinite;

  @keyframes spin {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }
`;

const LoaderText = styled.div`
  font-size: 0.875rem;
  color: ${COLORS.violet};
  font-weight: 500;
`;

const ResultContainer = styled.div`
  border-top: 1px solid ${COLORS.lightViolet};
  max-height: 150px;
  overflow-y: auto;
  scrollbar-width: thin;
  scrollbar-color: ${COLORS.lightGrey} transparent;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background-color: ${COLORS.lightGrey};
    border-radius: 10px;
  }
`;

const ResponseSection = styled.div`
  background-color: ${COLORS.lightestViolet};
  padding: 12px 16px;
  border-left: 3px solid ${COLORS.violet};
`;

const ResponseText = styled.div`
  font-size: 0.875rem;
  line-height: 1.4;
  white-space: pre-wrap;
  word-break: break-word;
`;

const ErrorSection = styled.div`
  background-color: ${COLORS.lightestRed};
  padding: 12px 16px;
  border-left: 3px solid ${COLORS.red};
`;

const ErrorText = styled.div`
  font-size: 0.875rem;
  line-height: 1.4;
  white-space: pre-wrap;
  word-break: break-word;
  color: ${COLORS.darkGrey};
`;

const ActionsBar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 16px;
  border-top: 1px solid ${COLORS.lightViolet};
  background-color: ${COLORS.white};
`;

const ActionButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 6px;
  border-radius: 6px;
  color: ${COLORS.violet};
  transition: background-color 0.2s, color 0.2s;

  &:hover {
    background-color: rgba(74, 37, 213, 0.1);
  }
`;

const StatusButtons = styled.div`
  display: flex;
  gap: 8px;
`;

const StatusButton = styled.button`
  background: ${(props) =>
    props.$active
      ? props.$success
        ? COLORS.green
        : props.$error
        ? COLORS.red
        : COLORS.grey
      : COLORS.white};
  border: 1px solid
    ${(props) =>
      props.$success ? COLORS.green : props.$error ? COLORS.red : COLORS.grey};
  color: ${(props) =>
    props.$active
      ? COLORS.white
      : props.$success
      ? COLORS.green
      : props.$error
      ? COLORS.red
      : COLORS.grey};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 6px;
  border-radius: 6px;
  transition: all 0.2s;

  &:hover {
    background-color: ${(props) =>
      props.$success
        ? COLORS.green
        : props.$error
        ? COLORS.red
        : COLORS.lightGrey};
    color: ${COLORS.white};
  }
`;
